// api/ProjectManager.js
import { createEntityManager, resolveVarsFromContext } from "./EntityManager";

import { loadOntology } from "./OntologyUtils";
import { getProfile } from "./ProfileOntologyManager";

/**
 * List projects owned by a user and return an array of project entities.
 * Returns: [ project, project, ... ]
 */
export async function listProjectsOwnedByUser({ userId, awsCreds }) {
  if (!userId) throw new Error("listProjectsOwnedByUser: 'userId' is required");
  if (!awsCreds)
    throw new Error("listProjectsOwnedByUser: 'awsCreds' is required");

  // 1) List via user ontology (owns → Project)
  const userOntology = await loadOntology("profile");
  const userMgr = createEntityManager(userOntology);
  const vars = resolveVarsFromContext(userMgr.meta, { userId });

  const items = await userMgr.list({
    relation: "owns",
    type: "Project",
    vars,
    awsCreds,
  });
  if (!items?.length) return [];

  // 2) Load each project via project ontology and return just the projects
  const projectOntology = await loadOntology("project");
  const projMgr = createEntityManager(projectOntology);

  const projects = await Promise.all(
    items.map(async (item) => {
      const ctx = { userId, projectUuid: item.id };
      const projVars = resolveVarsFromContext(projMgr.meta, ctx);
      return projMgr.get({ vars: projVars, awsCreds });
    }),
  );

  // Filter out any null/missing projects
  return projects.filter(Boolean);
}

// Assumes you already have these utilities/constants available somewhere:
// - loadOntology(YAML_STRING_OR_URL)
// - createEntityManager(ontology)
// - resolveVarsFromContext(meta, { userId, projectUuid, roomId? })

// ─────────────────────────────────────────────────────────────────────────────
// List rooms for a given project
// Returns: [ room, room, ... ]
// ─────────────────────────────────────────────────────────────────────────────
export async function listRoomsInProject({ userId, projectUuid, awsCreds }) {
  if (!userId) throw new Error("listRoomsInProject: 'userId' is required");
  if (!projectUuid)
    throw new Error("listRoomsInProject: 'projectUuid' is required");
  if (!awsCreds) throw new Error("listRoomsInProject: 'awsCreds' is required");

  // 1) Use the Project ontology to list via relation (contains → Room)
  const projectOntology = await loadOntology("project");
  const projMgr = createEntityManager(projectOntology);

  // Note: your Project YAML’s relation uses {COGNITO_ID} and {PROJECT_UUID}
  const projVars = resolveVarsFromContext(projMgr.meta, {
    userId,
    projectUuid,
  });

  const items = await projMgr.list({
    relation: "contains",
    type: "Room",
    vars: projVars,
    awsCreds,
  });

  console.log("projectOntology items: ", items);

  if (!items?.length) return [];

  // 2) For each listed room ID, load the full Room entity
  const roomOntology = await loadOntology("room");

  const roomMgr = createEntityManager(roomOntology);

  const rooms = await Promise.all(
    items.map(async (item) => {
      // `id_from: "folder"` → item.id is the immediate child folder name (room id)
      const roomVars = resolveVarsFromContext(roomMgr.meta, {
        userId,
        projectUuid,
        roomId: item.id, // ensure your Room ontology expects {ROOM_ID} or similar
      });
      return roomMgr.get({ vars: roomVars, awsCreds });
    }),
  );

  // Filter out any null/missing rooms
  return rooms.filter(Boolean);
}

export async function getRoomById({ userId, projectUuid, roomId, awsCreds }) {
  if (!userId) throw new Error("getRoomById: 'userId' is required");
  if (!projectUuid) throw new Error("getRoomById: 'projectUuid' is required");
  if (!roomId) throw new Error("getRoomById: 'roomId' is required");
  if (!awsCreds) throw new Error("getRoomById: 'awsCreds' is required");

  // Normalize roomId: handle cases like "room:project_xxx:home_office"
  const shortRoomId = roomId.includes(":") ? roomId.split(":").pop() : roomId;

  const roomOntology = await loadOntology("room");
  const roomMgr = createEntityManager(roomOntology);

  const vars = resolveVarsFromContext(roomMgr.meta, {
    userId,
    projectUuid,
    roomId: shortRoomId,
  });

  // console.log("Vars for getRoomById", vars);

  return roomMgr.get({ vars, awsCreds });
}
